/* PreviewBlock — resumo final do cadastro antes do envio.
 * Mostra os principais campos e estado das etapas.
 */
import { CheckCircle2, AlertOctagon, AlertTriangle, Circle, FileText } from "lucide-react";
import { STATE_META } from "@/lib/cadastroValidation";

const ICON_BY_STATE = {
  done: CheckCircle2,
  warning: AlertTriangle,
  blocked: AlertOctagon,
  progress: Circle,
  empty: Circle,
};

export default function PreviewBlock({ form, state, user, file }) {
  const src = form.source || {};
  const pestelList = (form.perspectiva_pestel || []).join(", ") || "—";
  const paises = form.pais_impacto || "—";

  return (
    <div className="space-y-5" data-testid="cadastro-preview">
      <div>
        <div className="label-mil text-[var(--gold)]">Etapa final · Prévia</div>
        <h3 className="font-display font-bold text-lg mt-1">Confirme antes de enviar</h3>
        <p className="text-[12px] text-[var(--text-muted)] mt-1 max-w-2xl">
          Confira abaixo o resumo do que será enviado para Revisão Humana. Você ainda pode voltar
          e editar qualquer bloco ou salvar como rascunho para concluir depois.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PreviewCard title="Fonte">
          <Row label="Título" value={src.titulo} testid="preview-source-titulo" />
          <Row label="Data" value={src.data_publicacao} />
          <Row label="Autores" value={src.autores} />
          <Row label="Origem" value={src.origem_fonte} />
          <Row label="Tipo" value={src.tipo_fonte} />
          <Row label="Idioma" value={src.idioma} />
        </PreviewCard>

        <PreviewCard title="Acesso">
          <Row label="Link original" value={src.link_original} />
          <Row label="DOI" value={src.doi} />
          <Row label="Link Drive" value={src.link_drive} />
          <Row label="Status do acesso" value={form.access_status} />
          <Row
            label="PDF anexo"
            value={file ? (
              <span className="inline-flex items-center gap-1">
                <FileText size={12} /> {file.name}
              </span>
            ) : null}
          />
        </PreviewCard>

        <PreviewCard title="Insumo">
          <Row label="Título do insumo" value={form.titulo_insumo} testid="preview-titulo-insumo" />
          <Row
            label="Citação direta"
            value={form.citacao_direta
              ? `${form.citacao_direta.slice(0, 180)}${form.citacao_direta.length > 180 ? "…" : ""}`
              : null}
          />
          <Row label="Página/Seção" value={form.pagina_ou_secao} />
        </PreviewCard>

        <PreviewCard title="Classificação">
          <Row label="Área temática" value={form.area_tematica_principal} />
          <Row label="Classificação" value={form.classificacao_semente} />
          <Row label="PESTEL" value={pestelList} />
          <Row label="Recorte" value={form.recorte_geografico} />
          <Row label="Países de impacto" value={paises} />
          <Row label="Horizonte" value={form.horizonte_temporal} />
        </PreviewCard>
      </div>

      <PreviewCard title="Responsável & Estado das Etapas">
        <Row label="Pesquisador" value={user?.name || user?.email || "—"} />
        <div className="pt-2">
          <ul className="grid grid-cols-2 md:grid-cols-3 gap-1.5" data-testid="preview-steps-list">
            {state.steps.map((s) => {
              const meta = STATE_META[s.state];
              const Icon = ICON_BY_STATE[s.state] || Circle;
              return (
                <li
                  key={s.id}
                  className={`flex items-center gap-2 text-[12px] px-2 py-1 rounded border ${meta.bg} ${meta.border} ${meta.text}`}
                  data-testid={`preview-step-${s.id}`}
                >
                  <Icon size={12} />
                  <span className="font-semibold">{s.label}</span>
                  <span className="opacity-75 ml-auto">{meta.label}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </PreviewCard>

      {(state.pendencies.length > 0 || state.warnings.length > 0) && (
        <div className="card-mil p-4 space-y-2 border-amber-300/50">
          <div className="font-display font-semibold text-sm">Pendências restantes</div>
          <ul className="space-y-1 text-[12.5px]">
            {state.pendencies.map((p) => (
              <li key={`p-${p}`} className="text-rose-700 dark:text-rose-300 flex items-start gap-1.5">
                <AlertOctagon size={11} className="mt-0.5 shrink-0" /> {p}
              </li>
            ))}
            {state.warnings.map((w) => (
              <li key={`w-${w}`} className="text-amber-700 dark:text-amber-300 flex items-start gap-1.5">
                <AlertTriangle size={11} className="mt-0.5 shrink-0" /> {w}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function PreviewCard({ title, children }) {
  return (
    <div className="card-mil p-4 space-y-1.5">
      <div className="font-display font-semibold text-sm text-[var(--gold)] uppercase tracking-wide">
        {title}
      </div>
      <dl className="space-y-1">{children}</dl>
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-2 text-[12.5px]" data-testid={testid}>
      <dt className="text-[var(--text-muted)]">{label}</dt>
      <dd className={`min-w-0 break-words ${value ? "" : "text-[var(--text-muted)] italic"}`}>
        {value || "— não informado"}
      </dd>
    </div>
  );
}
